import { supabase } from 'common/src/supabase';
import { Asker, Tasks, User } from '../utils/types';
import { nanoid } from 'nanoid';

export const fetchTasks = async (
  page?: number,
  limit?: number,
  sortOrder: 'asc' | 'desc' = 'desc',
  date?: string,
  assignedUserId?: number,
  status?: string
) => {
  try {
    let query = supabase
      .from('tasks')
      .select(
        `
        *,
        User (first_name, last_name, phone_number),
        asker_payments (id, payment_status)
      `,
        { count: 'exact' }
      )
      .order('created_at', { ascending: sortOrder === 'asc' });

    if (date) {
      query = query.gte('created_at', date);
    }

    if (assignedUserId) {
      query = query.eq('assigned_user_id', assignedUserId);
    }

    if (status) {
      query = query.eq('status', status);
    }

    if (page !== undefined && limit !== undefined) {
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;

    if (error) {
      throw new Error('No se pudieron obtener las Tasks');
    }

    // Process the data to include a hasPayment flag
    const processedData = data?.map((task) => ({
      ...task,
      hasPayment: task.asker_payments && task.asker_payments.length > 0,
      paymentStatus:
        task.asker_payments && task.asker_payments.length > 0
          ? task.asker_payments[0].payment_status
          : null,
    }));

    const totalPages =
      page !== undefined && limit !== undefined ? Math.ceil((count || 0) / limit) : 1;

    return { data: processedData, totalPages, totalCount: count };
  } catch (error) {
    console.error('Error al obtener tasks:', error);
    throw new Error('No se pudieron obtener las tasks');
  }
};

// TODO: React.Dispatch no deberia estar aca
export const subscribeToTasks = (setTasks: React.Dispatch<React.SetStateAction<Tasks[]>>) => {
  const subscription = supabase
    .channel('public:tasks')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, (payload) => {
      setTasks((prevTasks: Tasks[]) => {
        if (!prevTasks) return [payload.new as Tasks];
        const index = prevTasks.findIndex((task: Tasks) => task.id === (payload.new as Tasks).id);
        if (index === -1) {
          return [...prevTasks, payload.new as Tasks];
        } else {
          const updatedTasks = [...prevTasks];
          updatedTasks[index] = payload.new as Tasks;
          return updatedTasks;
        }
      });
    })
    .subscribe();

  return subscription;
};

export const getLeastBusyHelper = async (): Promise<User> => {
  try {
    const { data, error } = await supabase.rpc('get_least_busy_helper').single();

    if (error) throw error;
    return data as User;
  } catch (error) {
    console.error('Error getting least busy helper:', error);
    throw error;
  }
};

export const createTask = async (
  taskData: Omit<Tasks, 'id' | 'created_at'>,
  adminUserId?: number
) => {
  try {
    let assigned_user_id: number;

    if (adminUserId) {
      assigned_user_id = adminUserId;
    } else {
      const leastBusyHelper = await getLeastBusyHelper();
      assigned_user_id = leastBusyHelper.id;
    }

    const fullTaskData = {
      ...taskData,
      assigned_user_id: taskData.assigned_user_id || assigned_user_id,
      status: taskData.status || 'pending',
    };

    const { data, error } = await supabase.from('tasks').upsert(fullTaskData).select().single();

    if (error) {
      throw new Error('Could not create the Task');
    }

    return data;
  } catch (error) {
    console.error('Error creating task:', error);
    throw new Error('Failed to create task');
  }
};

export const deleteTask = async (taskId: number) => {
  try {
    const { error } = await supabase.from('tasks').delete().eq('id', taskId);

    if (error) {
      throw new Error('Could not delete the Task');
    }

    return true;
  } catch (error) {
    console.error('Error deleting task:', error);
    return false;
  }
};

export const updateTask = async (taskId: number, updatedData: Partial<Tasks>) => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .update(updatedData)
      .eq('id', taskId)
      .select();

    if (error) {
      console.error('Supabase error:', error);
      throw new Error(`Could not update the Task: ${error.message}`);
    }

    return data[0];
  } catch (error) {
    console.error('Error in updateTask:', error);
    throw new Error('Failed to update task');
  }
};

export const fetchTaskById = async (id: number): Promise<Tasks | null> => {
  const { data, error } = await supabase.from('tasks').select('*').eq('id', id).single();

  if (error) {
    console.error('Error fetching task:', error);
    throw error;
  }

  return data;
};

export const rejectTask = async (taskId: number, taskerId: string) => {
  try {
    const { error } = await supabase
      .from('task_offers')
      .update({ status: 'rejected' })
      .eq('task_id', taskId)
      .eq('tasker_id', taskerId);

    if (error) throw error;
  } catch (error) {
    console.error('Error rejecting task:', error);
    throw error;
  }
};

export const applyForTask = async (taskId: number, taskerId: string) => {
  try {
    const { data, error } = await supabase
      .from('task_offers')
      .update({ status: 'applied' })
      .eq('task_id', taskId)
      .eq('tasker_id', taskerId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error applying for task:', error);
    throw error;
  }
};

export const fetchTask = async (taskId: number): Promise<Tasks | null> => {
  try {
    const { data, error } = await supabase.from('tasks').select('*').eq('id', taskId).single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching task:', error);
    throw error;
  }
};

export const fetchTasksAdvanced = async (
  page?: number,
  limit?: number,
  sortOrder: 'asc' | 'desc' = 'desc',
  date?: string,
  assignedUserId?: number,
  status?: string,
  askerName?: string,
  askerLastName?: string,
  taskerName?: string,
  taskerLastName?: string
) => {
  try {
    let query = supabase
      .from('tasks')
      .select(
        `
        *,
        User!assigned_user_id (first_name, last_name, phone_number),
        asker:askers!asker_id (id, name, last_name, email, phone_number),
        asker_payments (id, payment_status)
      `,
        { count: 'exact' }
      )
      .neq('status', 'draft')
      .order('created_at', { ascending: sortOrder === 'asc' });

    if (date) {
      query = query.gte('created_at', date);
    }

    if (assignedUserId) {
      query = query.eq('assigned_user_id', assignedUserId);
    }

    if (status) {
      query = query.eq('status', status);
    }

    // Updated Asker filtering
    if (askerName || askerLastName) {
      // Build the askers query
      let askersQuery = supabase.from('askers').select('id');

      if (askerName) {
        askersQuery = askersQuery.ilike('name', `%${askerName}%`);
      }
      if (askerLastName) {
        askersQuery = askersQuery.ilike('last_name', `%${askerLastName}%`);
      }

      // Get matching askers
      const { data: matchingAskers } = await askersQuery;

      if (matchingAskers && matchingAskers.length > 0) {
        // Filter tasks by matching asker_ids
        query = query.in(
          'asker_id',
          matchingAskers.map((asker) => asker.id)
        );
      } else {
        // If no matching askers, return no results
        query = query.eq('asker_id', -1);
      }
    }

    // Tasker filtering
    if (taskerName || taskerLastName) {
      let taskerQuery = supabase.from('taskers').select('id');

      if (taskerName || taskerLastName) {
        const nameFilter = [];
        if (taskerName) nameFilter.push(`name.ilike.%${taskerName}%`);
        if (taskerLastName) nameFilter.push(`name.ilike.%${taskerLastName}%`);
        taskerQuery = taskerQuery.or(nameFilter.join(','));
      }

      const { data: taskerIds, error: taskerError } = await taskerQuery;

      if (taskerError) {
        console.error('Error fetching tasker IDs:', taskerError);
        throw new Error('Error fetching tasker IDs');
      }

      if (taskerIds && taskerIds.length > 0) {
        const taskOfferQuery = supabase
          .from('task_offers')
          .select('task_id')
          .in(
            'tasker_id',
            taskerIds.map((t) => t.id)
          );

        const { data: taskIds, error: taskOfferError } = await taskOfferQuery;

        if (taskOfferError) {
          console.error('Error fetching task IDs from task_offers:', taskOfferError);
          throw new Error('Error fetching task IDs from task_offers');
        }

        if (taskIds && taskIds.length > 0) {
          query = query.in(
            'id',
            taskIds.map((t) => t.task_id)
          );
        } else {
          query = query.eq('id', -1); // No results if no matching tasks
        }
      } else {
        query = query.eq('id', -1); // No results if no matching taskers
      }
    }

    if (page !== undefined && limit !== undefined) {
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching tasks:', error);
      throw new Error('No se pudieron obtener las Tasks');
    }

    // Process the data to include a hasPayment flag
    const processedData = data?.map((task) => ({
      ...task,
      hasPayment: task.asker_payments && task.asker_payments.length > 0,
      paymentStatus:
        task.asker_payments && task.asker_payments.length > 0
          ? task.asker_payments[0].payment_status
          : null,
    }));

    const totalPages =
      page !== undefined && limit !== undefined ? Math.ceil((count || 0) / limit) : 1;

    return { data: processedData, totalPages, totalCount: count };
  } catch (error) {
    console.error('Error al obtener tasks:', error);
    throw new Error('No se pudieron obtener las tasks');
  }
};

type CreateEmptyTaskParams = {
  askerData: Asker;
};

export const createEmptyTask = async ({ askerData }: CreateEmptyTaskParams) => {
  const accessCode = nanoid(8); // 8 character alphanumeric code

  const { data, error } = await supabase
    .from('tasks')
    .insert([
      {
        // asker related
        asker_id: askerData.id,
        name: askerData.name,
        last_name: askerData.last_name,
        phone_number: askerData.phone_number,
        email: askerData.email,

        status: 'draft',
        source: 'asker-web',
        is_asap: true,
        access_code: accessCode || null,
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getTasks = (startDate: string, endDate: string) => {
  return supabase
    .from('tasks')
    .select('*', { count: 'exact' })
    .gte('created_at', startDate)
    .lte('created_at', endDate);
};

export const getCompletedTask = (startDate: string, endDate: string) => {
    return supabase
      .from('tasks')
      .select('*', { count: 'exact' })
      .eq('status', 'complete')
      .gte('created_at', startDate)
      .lte('created_at', endDate);
  };

  export const getTaskAssigned = (filter: string) => {
    let query = supabase
    .from('tasks')
    .select(
      `
      *,
      asker:askers(name, email),
      task_offers!inner(id, tasker_id, status, paid)
    `
    )
    .eq('status', 'complete')
    .eq('task_offers.status', 'assigned')
    // .gte('date', '2024-10-08')
    .order('date', { ascending: false });
    if (filter === 'pending') {
      query = query.eq('task_offers.paid', false);
    }
    return query
  }

  export const fetchOneTaskWithAsker = (taskId: number) => {
    return supabase.from('tasks').select('*, asker:asker_id (*)').eq('id', taskId).single();
  };
 
  export const fetchTaskRecurrents = () => {
    return supabase
    .from('tasks')
    .select('*')
    .eq('is_recurring', true)
    .order('id', { ascending: false });
  }

  export const fetchTaskReturn = (taskId: number) => { 
    return supabase
  .from('tasks')
  .select('*')
  .eq('id', taskId)
  .single();
}

export const fetchTaskRejectedAndDraft = (startDate: string, endDate: string) => {
  return supabase
  .from('tasks')
  .select('*')
  .neq('status', 'draft')
  .neq('status', 'rejected')
  .gte('created_at', startDate)
  .lte('created_at', endDate);
}

export const fetchTaskDraft = (startDate: string, endDate: string) => {
  return supabase.from('tasks')
  .select('*')
  .neq('status', 'draft')
  .gte('created_at', startDate)
  .lte('created_at', endDate);
}

export const fetchAllTaskComplete = () => {
  return supabase.from('tasks').select('price').eq('status', 'complete');
}